import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import RfpPreview from '@/components/RfpPreview';
import RfpFeedbackDialog from '@/components/RfpFeedbackDialog';
import CategoryDecisionsPanel from '@/components/CategoryDecisionsPanel';
import { RiskScoreCard, RiskBadge, RiskAttestationMarker } from '@/components/RiskScoreCard';
import MasterReferenceAdvisoryBanner from '@/components/MasterReferenceAdvisoryBanner';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  ArrowLeft, 
  ArrowRight,
  Loader2,
  FileText,
  ShieldCheck,
  MessageSquare,
  ClipboardList,
  Play,
  CheckCircle,
  Clock,
  Edit,
  Upload,
  Trash2,
  FileSpreadsheet
} from 'lucide-react';
import { toastSuccess, toastError } from '@/lib/toast';
import api from '@/lib/api';
import { resolveProcurementType } from '@/components/wizard/WizardContext';
import { orderQuestionsForDisplay } from '@/lib/questionDisplay';
import { formatDate } from '@/lib/utils';
import { getStatusLabel } from '@/lib/statusLabels';
import { keptEntries } from '@/lib/vendorQuestionRemoval';
import { ParsedRequirementGrid, useCategoryLookup } from '@/components/ParsedRequirementFields';

// Workflow steps configuration.
//
// INTAKE COMPRESSION V1 (2026-08-20): the 'uploads' entry is removed, in
// step with WIZARD_STEPS - the two upload boxes moved onto Intake. This
// list is display-only (the read-only progress banner); it is NOT the
// wizard's navigation model. It is kept in the same ORDER and with the
// same keys as WIZARD_STEPS so a reader comparing the two pages sees one
// workflow, and stepModel.parity.test.jsx pins that key sequence.
const WORKFLOW_STEPS = [
  { key: 'intake', label: 'Intake', status: 'intake' },
  { key: 'questions', label: 'Questions', status: 'questions_generated' },
  { key: 'answers', label: 'Answers', status: 'answers_provided' },
  { key: 'review', label: 'Review', status: 'answers_provided' },
  { key: 'items', label: 'Line Items', status: 'refined' },
  { key: 'rfp', label: 'RFP', status: 'rfp_generated' },
  { key: 'risk', label: 'Risk', status: 'completed' }
];

// Map status to the LAST STEP DONE, for the read-only progress banner.
//
// THE DIVERGENCE FROM WizardContext's SAME-NAMED FUNCTION IS INTENDED AND
// IS NOT A BUG. That one answers "the next step to do" and drives
// navigation; this one answers "how far has this got" and drives a
// display. They agree on some statuses and differ on others, and
// stepModel.parity.test.jsx pins BOTH tables against literals with the
// difference declared, so neither can drift silently. Do not unify them.
//
// answers_need_correction ADDED (defect fixed in passing, 2026-08-21).
// It was missing, so a procurement blocked at Answers fell through to
// `default` and this page reported it as sitting at Intake - a known-false
// mapping. Leaving it in a function this commit is already rewriting would
// have been shipping a stale claim; it is called out in the commit body
// rather than folded in silently.
const getStepFromStatus = (status) => {
  switch (status) {
    case 'intake': return 0;
    case 'questions_generated': return 1;
    case 'answers_need_correction': return 2;
    case 'answers_provided': return 2;
    case 'refined': return 4;
    case 'rfp_generated': return 5;
    case 'completed': return 6;
    default: return 0;
  }
};

// Get next action description
const getNextAction = (status) => {
  const actions = {
    'intake': 'Upload documents and generate questions',
    'questions_generated': 'Answer business questions',
    'answers_provided': 'Review and finalize vendor questions',
    'refined': 'Edit line items and generate RFP',
    'rfp_generated': 'Generate risk intelligence',
    'completed': 'All steps completed'
  };
  return actions[status] || 'Continue workflow';
};

// Progress indicator component
const WorkflowProgress = ({ status }) => {
  const currentStep = getStepFromStatus(status);
  const isCompleted = status === 'completed';
  
  return (
    <div className="bg-white border border-zinc-200 p-6 mb-6" data-testid="detail-workflow-status">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-1">
            Workflow Status
          </div>
          <div className={`text-lg font-semibold ${isCompleted ? 'text-green-600' : 'text-[#0055FF]'}`}>
            {getStatusLabel(status)}
          </div>
        </div>
        {isCompleted ? (
          <CheckCircle className="w-8 h-8 text-green-500" />
        ) : (
          <Clock className="w-8 h-8 text-[#0055FF]" />
        )}
      </div>
      
      {/* Progress bar */}
      <div className="relative">
        <div className="flex justify-between mb-2">
          {WORKFLOW_STEPS.map((step, index) => (
            <div 
              key={step.key}
              className={`text-xs font-medium ${
                index <= currentStep ? 'text-[#0055FF]' : 'text-zinc-400'
              }`}
            >
              {index + 1}
            </div>
          ))}
        </div>
        <div className="h-2 bg-zinc-200 rounded-full overflow-hidden">
          <div 
            className="h-full bg-[#0055FF] transition-all duration-500"
            style={{ width: `${((currentStep + 1) / WORKFLOW_STEPS.length) * 100}%` }}
          />
        </div>
        <div className="flex justify-between mt-1">
          {WORKFLOW_STEPS.map((step, index) => (
            <div 
              key={step.key}
              className={`text-[10px] ${
                index <= currentStep ? 'text-zinc-600' : 'text-zinc-400'
              } ${index === 0 ? 'text-left' : index === WORKFLOW_STEPS.length - 1 ? 'text-right' : 'text-center'}`}
              style={{ width: `${100 / WORKFLOW_STEPS.length}%` }}
            >
              {step.label}
            </div>
          ))}
        </div>
      </div>
      
      {/* Next action hint */}
      {!isCompleted && (
        <div className="mt-4 pt-4 border-t border-zinc-100">
          <div className="flex items-center gap-2 text-sm text-zinc-600">
            <ArrowRight className="w-4 h-4 text-[#0055FF]" />
            <span><strong>Next:</strong> {getNextAction(status)}</span>
          </div>
        </div>
      )}
    </div>
  );
};

const ProcurementDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [procurement, setProcurement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const categoryLookup = useCategoryLookup();
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);

  const fetchProcurement = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.getProcurement(id);
      setProcurement(response.data);
    } catch (err) {
      setError('Failed to load procurement details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchProcurement();
  }, [fetchProcurement]);

  // THE BOQ ROW CAP, for the manifest card below (2026-09-07). Read from
  // the same procurement-free template route the editable intake card
  // reads (FileUploadSection.jsx `rowCap`) - agents.INTAKE_BOQ_ROW_CAP -
  // never a literal here. null until it arrives, and the cap clause
  // simply does not render until then; it never guesses. Fetched only
  // when there is a BOQ to describe: a procurement without one has no
  // sentence to complete and makes no request.
  const boqItemCount = procurement?.boq_items?.length || 0;
  const [boqRowCap, setBoqRowCap] = useState(null);
  useEffect(() => {
    if (boqItemCount === 0) return undefined;
    let alive = true;
    (async () => {
      try {
        const response = await api.getBoqTemplate();
        const cap = response?.data?.row_cap;
        if (alive && Number.isFinite(cap)) setBoqRowCap(cap);
      } catch (err) {
        // No cap clause rather than a wrong one.
      }
    })();
    return () => { alive = false; };
  }, [boqItemCount]);


  // Navigate to continue the workflow
  const handleContinue = () => {
    // Navigate to the new procurement page with the procurement ID
    // The NewProcurement page will need to handle resuming from a specific step
    navigate(`/continue/${id}`);
  };

  // "Edit" (Original Requirement card) - trace finding (2026-07-26): this
  // used to just call handleContinue, an exact duplicate of the page's own
  // Continue button - it never opened any editor, just resumed wherever
  // getStepFromStatus landed (Answers/Review/RFP/etc, never Intake). The
  // real intake edit flow (StepIntake.jsx's read-only view -> Edit trigger
  // -> confirm dialog -> unlockStepForEdit -> re-parse) only ever rendered
  // if the user manually clicked "Back" all the way to step 0. Fix: signal
  // ContinueProcurement to land on step 0 explicitly via router navigation
  // state - transient (this session-history entry only, cleared by
  // ContinueProcurement right after reading it), so a refresh of /continue
  // resumes by status normally, same as landing here any other way. This
  // does NOT unlock or bypass the confirmation dialog - it only changes
  // which step renders; StepIntake's own read-only-view + ReadOnlyStepBanner
  // Edit trigger still gate the actual edit action exactly as before.
  const handleEditRequirement = () => {
    navigate(`/continue/${id}`, { state: { targetStep: 'intake' } });
  };

  // Handle delete procurement
  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this procurement? This action cannot be undone.')) {
      return;
    }
    
    setDeleting(true);
    try {
      await api.deleteProcurement(id);
      toastSuccess('Procurement deleted successfully');
      navigate('/');
    } catch (err) {
      // DELETE FREEZE GUARD (2026-07-27): surface the backend's 409
      // message ("This procurement is completed and can no longer be
      // deleted.") verbatim rather than a generic fallback.
      const detail = err?.response?.data?.detail;
      toastError(typeof detail === 'string' ? detail : 'Failed to delete procurement');
      setDeleting(false);
    }
  };
  
  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 text-[#0055FF] animate-spin" />
        </div>
      </Layout>
    );
  }
  
  if (error || !procurement) {
    return (
      <Layout>
        <div className="p-6 md:p-12">
          <div className="text-center py-16">
            <p className="text-zinc-500 mb-4">{error || 'Procurement not found'}</p>
            <Link to="/">
              <Button variant="outline">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
            </Link>
          </div>
        </div>
      </Layout>
    );
  }
  
  const isCompleted = procurement.status === 'completed';

  // Version-aware (RFP can regenerate to a new version under P2): feedback
  // only reads as "received" when it was given for the CURRENT rfp_version.
  const feedbackSubmittedVersion = procurement.feedback_submitted_version ?? null;
  const currentRfpVersion = procurement.rfp_version || 1;
  const feedbackIsCurrent = feedbackSubmittedVersion != null && feedbackSubmittedVersion === currentRfpVersion;
  const feedbackWasGivenOnOlderVersion = feedbackSubmittedVersion != null && !feedbackIsCurrent;

  // Prefer final (post-user-edit) vendor questions, matching what's actually
  // in the RFP; fall back to earlier stages if the procurement hasn't reached
  // that point yet. revised_vendor_questions items are {original, revised}
  // objects, not strings, so they need unwrapping.
  //
  // F5 (2026-08-29): an entry the agent declared REMOVED under an
  // applicability verdict never reaches the RFP, so it must not be listed
  // here either - this fallback renders before finalize-questions has run,
  // and showing a removed question as one of the RFP's vendor questions would
  // contradict the Review screen the buyer just saw.
  const keptRevisedVendorQuestions = keptEntries(procurement.revised_vendor_questions || []);
  const displayVendorQuestions = procurement.final_vendor_questions?.length > 0
    ? procurement.final_vendor_questions
    : keptRevisedVendorQuestions.length > 0
      ? keptRevisedVendorQuestions.map(q => (q && typeof q === 'object') ? (q.revised || q.original || '') : q)
      : (procurement.vendor_risk_questions || []);

  return (
    <Layout>
      <div className="p-6 md:p-12 animate-fade-in">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/" 
            className="inline-flex items-center text-zinc-500 hover:text-zinc-900 text-sm mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Dashboard
          </Link>
          
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-zinc-900 mb-2">
                Procurement Details
              </h1>
              <p className="text-zinc-500 text-sm">
                Created {formatDate(procurement.created_at)}
              </p>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                {procurement.serial_id && (
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-zinc-600 bg-zinc-100 px-2.5 py-1 rounded">
                    Request Number:
                    <span className="font-semibold text-zinc-900">{procurement.serial_id}</span>
                  </span>
                )}
                {procurement.rfp_document && procurement.serial_id && (
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-[#0055FF] bg-blue-50 px-2.5 py-1 rounded">
                    RFP Number:
                    <span className="font-semibold">{`RFP_${procurement.serial_id}_v${procurement.rfp_version || 1}`}</span>
                  </span>
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {procurement.risk_assessment && (
                <RiskBadge
                  level={procurement.risk_assessment.risk_level}
                  score={procurement.risk_assessment.risk_score}
                />
              )}
              <RiskAttestationMarker riskAssessment={procurement.risk_assessment} compact />
              
              {/* Delete Button - DELETE FREEZE GUARD (2026-07-27, Task 7
                  merge-review item 1): hidden once completed, since the
                  backend now rejects it with a 409. */}
              {!isCompleted && (
                <Button
                  variant="outline"
                  onClick={handleDelete}
                  disabled={deleting}
                  className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                  data-testid="delete-procurement-btn"
                >
                  {deleting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4 mr-2" />
                  )}
                  {deleting ? 'Deleting...' : 'Delete'}
                </Button>
              )}

              {/* Continue Button - Show if not completed */}
              {!isCompleted && (
                <Button
                  onClick={handleContinue}
                  className="bg-[#0055FF] hover:bg-[#0044CC] text-white"
                  data-testid="continue-procurement-btn"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Continue Procurement
                </Button>
              )}

              {/* Feedback Button - Show only when procurement is completed and
                  feedback hasn't been submitted for the current RFP version */}
              {isCompleted && (
                feedbackIsCurrent ? (
                  <span className="inline-flex items-center gap-1.5 text-sm text-zinc-500" data-testid="feedback-received-note">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Feedback received
                  </span>
                ) : (
                  <Button
                    onClick={() => setShowFeedbackDialog(true)}
                    className="bg-[#0055FF] hover:bg-[#0044CC] text-white"
                    data-testid="send-feedback-btn"
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    {feedbackWasGivenOnOlderVersion ? 'Share feedback on the updated RFP' : 'Send Feedback on this RFP'}
                  </Button>
                )
              )}
            </div>
          </div>
        </div>

        <RfpFeedbackDialog
          open={showFeedbackDialog}
          onClose={() => setShowFeedbackDialog(false)}
          procurementId={procurement.id}
          category={procurement.refined_requirement?.buyer_input?.category || procurement.refined_requirement?.buyer_input?.l1_category || 'General'}
          onSubmitComplete={(submittedForVersion) => {
            if (submittedForVersion != null) {
              setProcurement(prev => (prev ? { ...prev, feedback_submitted_version: submittedForVersion } : prev));
            }
            setShowFeedbackDialog(false);
          }}
        />
        
        {/* THE RESUME ADVISORY on the detail page (staleness gate, commit 5):
            GET /procurement/{id}'s master_reference_advisory, informing only.
            PLACEMENT (Ashok ruling, 2026-09-17): directly under the page header
            and ABOVE Workflow Status - beside the Continue Procurement button it
            warns about. It used to sit below the Bill of Quantities, so a buyer
            saw a clean status and pressed Continue without scrolling to it. */}
        <MasterReferenceAdvisoryBanner
          advisory={procurement.master_reference_advisory}
          totalLines={Array.isArray(procurement.final_line_items) ? procurement.final_line_items.length : null}
          className="mb-6"
        />

        {/* Workflow Progress */}
        <WorkflowProgress status={procurement.status} />
        
        {/* Original Requirement */}
        <div className="bg-white border border-zinc-200 p-6 mb-8">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
              Original Requirement
            </div>
            {!isCompleted && (
              <Button
                variant="ghost"
                size="sm"
                className="text-zinc-500 hover:text-zinc-900"
                onClick={handleEditRequirement}
                data-testid="edit-original-requirement-btn"
              >
                <Edit className="w-3 h-3 mr-1" />
                Edit
              </Button>
            )}
          </div>
          <p className="text-zinc-700 leading-relaxed">{procurement.user_requirement}</p>
        </div>
        
        {/* Parsed Requirement - Show if available */}
        {procurement.parsed_requirement && (
          <div className="bg-white border border-zinc-200 p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
                Parsed Requirement
              </div>
              {procurement.attached_documents && procurement.attached_documents.filter(d => !d.is_deleted).length > 0 && (
                <div className="flex items-center gap-1.5 text-xs text-[#0055FF] bg-blue-50 px-2 py-1 rounded">
                  <FileText className="w-3 h-3" />
                  <span>Detailed specs attached below</span>
                </div>
              )}
            </div>
            <ParsedRequirementGrid parsedRequirement={procurement.parsed_requirement} categoryLookup={categoryLookup} />
          </div>
        )}
        
        {/* Bill of Quantities - BUYER PROVENANCE (2026-09-07).
            Two attachment cards sit here side by side and they are NOT the
            same kind of thing. The documents below are what the buyer SENDS
            to vendors: the RFP names them in its Supporting Documents
            section (utils/document_generator.py, RfpPreview.jsx). The BOQ
            is what the buyer KEEPS: the schedule in the RFP IS the BOQ
            rendered, and its source filename tells a vendor nothing they
            can price. Ruled by Ashok, 2026-09-07: the RFP must NOT name the
            BOQ file - that would be an internal artefact leaking into a
            vendor document. document_generator.py carries no boq reference
            today and that is the correct state, not a gap. Do not add the
            filename to the RFP for symmetry with the documents table.

            THIS IS A MANIFEST, NOT A RETRIEVAL POINT, and so is the
            documents card. Uploads persist no bytes (upload_document stores
            a storage_path label and the extracted text, nothing else) and
            GET .../document/{id}/download returns JSON metadata. By the
            same ruling that is by design: attachments are never embedded
            in the RFP, never saved with it, and no combined package is
            produced - sending documents to vendors is the buyer's offline
            act. Neither card offers a download, and neither should imply
            one.

            WHAT IT SAYS: the filename (the fact nothing else on this page
            carries - a BOQ is replace-only, so a buyer who suspects the
            wrong spreadsheet was attached has no other way to check) and
            the parsed count. Above the cap the count carries the cap too,
            from the server value fetched above - on a completed
            procurement that is the fact explaining the RFP's line count.
            Same rule as the editable card's headline (FileUploadSection.
            jsx `boq-loaded-headline`): clause only above the cap, never a
            literal, omitted when the cap is unknown. The provenance line is
            the SAME SENTENCE as the other three copies (FileUploadSection,
            StepIntake read-only, LineItemsEditor) and is pinned to them by
            ProcurementDetail.boqSource.test.jsx. A deliberate duplicate,
            registered in CLAUDE.md's mirror table: StepIntake's copy is
            inline JSX in a step component and extracting it restructures
            a file this slice must not touch.

            THE ABSENT ARMS: no boq_items, no card - not an empty section.
            boq_items with NO boq_source (documents uploaded before d0a3acf,
            2026-07-30, added the field) render the count alone, never an
            empty "Source:". Guarded on non-blank CONTENT, not truthiness,
            as StepIntake's copy is: a whitespace-only filename is a truthy
            string and would otherwise render a gap where the answer
            should be. */}
        {boqItemCount > 0 && (
          <div className="bg-white border border-zinc-200 p-6 mb-8" data-testid="detail-boq-section">
            <div className="flex items-center gap-2 mb-3">
              <FileSpreadsheet className="w-4 h-4 text-zinc-500" />
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
                Bill of Quantities
              </div>
            </div>
            <p className="text-sm text-zinc-700" data-testid="detail-boq-headline">
              {boqItemCount} items loaded
              {boqRowCap !== null && boqItemCount > boqRowCap ? ` — the analysis read the first ${boqRowCap}` : ''}
            </p>
            {String(procurement.boq_source?.filename || '').trim() && (
              <p className="mt-1 text-xs text-zinc-500" data-testid="detail-boq-provenance">
                Source: {procurement.boq_source.filename}
              </p>
            )}
          </div>
        )}

        {/* Attached Documents - Show if available */}
        {procurement.attached_documents && procurement.attached_documents.length > 0 && (
          <div className="bg-white border border-zinc-200 p-6 mb-8">
            <div className="flex items-center gap-2 mb-3">
              <Upload className="w-4 h-4 text-zinc-500" />
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
                Attached Documents ({procurement.attached_documents.filter(d => !d.is_deleted).length})
              </div>
            </div>
            <div className="space-y-2">
              {procurement.attached_documents
                .filter(doc => !doc.is_deleted)
                .map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between py-2 px-3 bg-zinc-50 rounded">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-zinc-400" />
                      <span className="text-sm text-zinc-700">{doc.filename}</span>
                      <span className="text-xs text-zinc-500 bg-zinc-200 px-2 py-0.5 rounded">
                        {doc.document_type}
                      </span>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}
        
        {/* Tabs */}
        <Tabs defaultValue="rfp" className="w-full">
          <TabsList className="bg-zinc-100 p-1 mb-6">
            <TabsTrigger 
              value="rfp" 
              className="data-[state=active]:bg-white data-[state=active]:text-zinc-900"
              data-testid="tab-rfp"
            >
              <FileText className="w-4 h-4 mr-2" />
              RFP Document
            </TabsTrigger>
            <TabsTrigger 
              value="risk"
              className="data-[state=active]:bg-white data-[state=active]:text-zinc-900"
              data-testid="tab-risk"
            >
              <ShieldCheck className="w-4 h-4 mr-2" />
              Risk Intelligence
            </TabsTrigger>
            <TabsTrigger
              value="qa"
              className="data-[state=active]:bg-white data-[state=active]:text-zinc-900"
              data-testid="tab-qa"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Q&A
            </TabsTrigger>
            {/* BUYER DECISIONS (C1b-1b-ii, Ashok ruling 2026-08-17) - a
                fourth PEER tab, deliberately not a section inside
                another. Tab selection here is uncontrolled local state
                (defaultValue, no value/onValueChange, no URL param), so a
                fourth tab costs no navigational plumbing and changes no
                state model. It is separate from the RFP tab on purpose:
                that tab shows what the VENDOR receives, and this shows
                the internal decision record excluded from it.

                THIS TAB IS A CONTAINER, NOT A SINGLE REPORT. It holds
                buyer decision records, and after C2 it will also carry
                LOCATION decisions. Each panel names its own scope in its
                own heading ("Line Level Category Decisions"), so a second
                panel added beneath this one needs no change here. Location
                gets its OWN panel under this tab - NOT a merged table:
                the two decisions have different vocabularies and different
                producers, and one table would force a lowest-common
                shape on both. */}
            <TabsTrigger
              value="provenance"
              className="data-[state=active]:bg-white data-[state=active]:text-zinc-900"
              data-testid="tab-provenance"
            >
              <ClipboardList className="w-4 h-4 mr-2" />
              Buyer Decisions
            </TabsTrigger>
          </TabsList>
          
          {/* RFP Tab */}
          <TabsContent value="rfp">
            {procurement.rfp_document ? (
              <RfpPreview
                rfpDocument={procurement.rfp_document}
                procurementId={procurement.id}
                refinedRequirement={procurement.refined_requirement}
                serialId={procurement.serial_id}
                procurementLineItems={
                  // Bug batch: an empty final_line_items ([], truthy in JS)
                  // must not win over the populated AI baseline.
                  procurement.final_line_items?.length > 0
                    ? procurement.final_line_items
                    : procurement.requirement_line_items
                }
                attachedDocuments={procurement.attached_documents}
                isCompletionStep={isCompleted}
              />
            ) : (
              <div className="bg-white border border-zinc-200 p-12 text-center">
                <FileText className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
                <p className="text-zinc-500 mb-4">RFP document not generated yet</p>
                {!isCompleted && (
                  <Button
                    onClick={handleContinue}
                    className="bg-[#0055FF] hover:bg-[#0044CC] text-white"
                    data-testid="rfp-tab-continue-btn"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    {/* Bug batch: this empty-state button shows whenever
                        there's no rfp_document yet, at any pre-RFP status
                        (e.g. still at Review) - a hardcoded "Generate RFP"
                        label was misleading there. handleContinue already
                        resumes at the correct step via getStepFromStatus;
                        only the label was wrong. */}
                    {getNextAction(procurement.status)}
                  </Button>
                )}
              </div>
            )}
          </TabsContent>
          
          {/* Risk Tab */}
          <TabsContent value="risk">
            {procurement.risk_assessment ? (
              <div className="bg-white border border-zinc-200 p-8">
                <RiskScoreCard riskAssessment={procurement.risk_assessment} />
              </div>
            ) : (
              <div className="bg-white border border-zinc-200 p-12 text-center">
                <ShieldCheck className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
                <p className="text-zinc-500 mb-4">Risk Intelligence not completed yet</p>
                {!isCompleted && (
                  <Button onClick={handleContinue} className="bg-[#0055FF] hover:bg-[#0044CC] text-white">
                    <Play className="w-4 h-4 mr-2" />
                    Continue to Risk Intelligence
                  </Button>
                )}
              </div>
            )}
          </TabsContent>
          
          {/* Q&A Tab */}
          <TabsContent value="qa">
            <div className="bg-white border border-zinc-200 p-6">
              <div className="grid md:grid-cols-2 gap-8">
                {/* Business Questions */}
                <div>
                  <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-4">
                    Business Questions
                  </div>
                  {procurement.business_questions?.length > 0 ? (
                    <div className="space-y-4">
                      {/* THE THIRD CONSUMER OF THE SHARED ORDER (Ashok ruling
                          2026-09-03). This list numbered by stored index while
                          the wizard was about to number by display order -
                          the same question would have read "3." in the wizard
                          and "7." here, on the surface a buyer consults after
                          issue. Same helper, same numbers, same labels; the
                          answer is still read by the STORED index. */}
                      {orderQuestionsForDisplay(
                        procurement.business_questions,
                        resolveProcurementType({
                          docProcurementType: procurement.procurement_type,
                          refinedRequirement: procurement.refined_requirement,
                          parsedRequirement: procurement.parsed_requirement,
                        }),
                        Boolean(procurement.boq_items && procurement.boq_items.length > 0),
                      ).map(({ index: i, number, label }) => (
                        <div key={`bq-${i}-${label.slice(0, 20)}`} className="border-b border-zinc-100 pb-4 last:border-0" data-testid={`detail-question-${i}`}>
                          <p className="text-sm text-zinc-700 font-medium mb-2">
                            <span className="text-[#0055FF] mr-2">{number}.</span>
                            {label}
                          </p>
                          {procurement.user_answers?.[`q${i}`] && (
                            <p className="text-sm text-zinc-600 bg-zinc-50 p-3 rounded-sm">
                              {procurement.user_answers[`q${i}`]}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-zinc-500 text-sm">No questions generated yet</p>
                  )}
                </div>
                
                {/* Vendor Questions */}
                <div>
                  <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-4">
                    Vendor Questions
                  </div>
                  {displayVendorQuestions.length > 0 ? (
                    <div className="space-y-3">
                      {displayVendorQuestions.map((q, i) => (
                        <div key={`vq-${i}-${q.slice(0, 20)}`} className="flex items-start gap-2 text-sm text-zinc-600">
                          <span className="text-zinc-400 font-bold">{i + 1}.</span>
                          <span>{q}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-zinc-500 text-sm">No vendor questions yet</p>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Buyer Decisions Tab - the INTERNAL audit record. Reads
              final_line_items directly; RfpPreview is deliberately not
              involved, because its contract is "this is what the vendor
              will receive" and a reviewer must be able to trust that
              without a prop changing what the screen means. */}
          <TabsContent value="provenance">
            <CategoryDecisionsPanel lineItems={procurement.final_line_items} />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default ProcurementDetail;
